import React, { useMemo, useState } from "react";
import {
    Mafs,
    Coordinates,
    Plot,
    Point,
    Theme,
    Line,
    Polygon,
    MovablePoint,
    Text as MafsText,
} from "mafs";
import { Card, Space, Typography, Segmented, Row, Statistic, Tag, Alert } from "antd";
import { ThunderboltOutlined, AreaChartOutlined, LineChartOutlined } from "@ant-design/icons";

const { Title, Text: AntText } = Typography;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const finiteOr = (v, fb) => (Number.isFinite(v) ? v : fb);

function derivedFunctionType(baseType, studentFor) {
    // minimal mapping so sine/cosine student curves render plausibly
    if (baseType === "SINE" || baseType === "COSINE") {
        // derivative(sin)=cos, integral(sin)=-cos; derivative(cos)=-sin, integral(cos)=sin
        return baseType === "SINE" ? "COSINE" : "SINE";
    }
    return baseType || "POLYNOMIAL";
}

function makeEngine(functionType, coefficients) {
    const type = functionType || "POLYNOMIAL";
    const coeffs = Array.isArray(coefficients) ? coefficients.map(Number) : [];

    // Polynomial helpers (descending order)
    const poly = (x) =>
        coeffs.reduce((acc, c, i) => {
            const p = coeffs.length - 1 - i;
            return acc + c * Math.pow(x, p);
        }, 0);

    const polyPrime = (x) =>
        coeffs.slice(0, -1).reduce((acc, c, i) => {
            const p = coeffs.length - 1 - i;
            return acc + c * p * Math.pow(x, p - 1);
        }, 0);

    const polyIntegral = (x) =>
        coeffs.reduce((acc, c, i) => {
            const p = coeffs.length - i; // +1 in degree
            return acc + (c / p) * Math.pow(x, p);
        }, 0);

    // Sine/Cosine: [A, k, phase, shift]
    const A = finiteOr(coeffs[0], 1);
    const k = finiteOr(coeffs[1], 1);
    const phase = finiteOr(coeffs[2], 0);
    const shift = finiteOr(coeffs[3], 0);
    const safeK = Math.abs(k) < 1e-6 ? 1e-6 : k;

    if (type === "SINE") {
        const f = (x) => A * Math.sin(k * x + phase) + shift;
        const fPrime = (x) => A * k * Math.cos(k * x + phase);
        const F = (x) => (-A / safeK) * Math.cos(k * x + phase) + shift * x;
        return { f, fPrime, F };
    }

    if (type === "COSINE") {
        const f = (x) => A * Math.cos(k * x + phase) + shift;
        const fPrime = (x) => -A * k * Math.sin(k * x + phase);
        const F = (x) => (A / safeK) * Math.sin(k * x + phase) + shift * x;
        return { f, fPrime, F };
    }

    // PARABOLA behaves like polynomial (expect [a,b,c])
    return { f: poly, fPrime: polyPrime, F: polyIntegral };
}

function buildAreaPolygon(f, x0, samples = 80) {
    const end = Number(x0);
    if (!Number.isFinite(end) || Math.abs(end) < 1e-12) return null;

    const a = Math.min(0, end);
    const b = Math.max(0, end);
    const pts = [];

    pts.push([a, 0]);
    for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const x = a + (b - a) * t;
        const y = f(x);
        if (Number.isFinite(y)) pts.push([x, y]);
    }
    pts.push([b, 0]);

    return pts;
}

export const CalculusGrapher = ({ config, content }) => {
    const cfg = config || {};
    const explanation =
        content?.explanation ||
        "Explore the link between a function, its derivative (slope), and its integral (accumulated area).";

    const displayMode = cfg.displayMode || "TRIPLE_STACK";
    const functionType = cfg.functionType || "POLYNOMIAL";

    const xMin = finiteOr(cfg.rangeXMin, -5);
    const xMax = finiteOr(cfg.rangeXMax, 5);
    const yMin = finiteOr(cfg.rangeYMin, -10);
    const yMax = finiteOr(cfg.rangeYMax, 10);

    const viewBox = { x: [xMin, xMax], y: [yMin, yMax] };

    const hasStudent = Array.isArray(cfg.studentCoefficients) && cfg.studentCoefficients.length > 0;
    const studentFor = cfg.studentCoefficientsFor || "DERIVATIVE";

    const [viewMode, setViewMode] = useState("CORRECT");
    const [x0, setX0] = useState(() => clamp(0, xMin, xMax));

    const dp = Number.isInteger(cfg.precisionDp) ? clamp(cfg.precisionDp, 0, 4) : 2;
    const format = (n) => {
        const x = Number(n);
        return Number.isFinite(x) ? Number(x.toFixed(dp)) : 0;
    };

    // Correct engine always based on base coefficients = f(x)
    const baseCoeffs = Array.isArray(cfg.coefficients) ? cfg.coefficients : [1, 0, 0];
    const correctEngine = useMemo(() => makeEngine(functionType, baseCoeffs), [functionType, baseCoeffs]);

    // Student curve engine is only used as a direct plotted curve in the target pane (f' or F)
    const studentType = derivedFunctionType(functionType, studentFor);
    const studentCurveEngine = useMemo(() => {
        if (!hasStudent) return null;
        return makeEngine(studentType, cfg.studentCoefficients);
    }, [hasStudent, studentType, cfg.studentCoefficients]);

    const showDerivativePane = displayMode !== "INTEGRAL_FUNCTION";
    const showIntegralPane = displayMode !== "FUNCTION_DERIVATIVE";

    const showTangent = cfg.showTangent !== false;
    const showArea = cfg.showAreaShading !== false;

    // f(x) always correct
    const safeF = (x) => clamp(finiteOr(correctEngine.f(x), 0), yMin * 4, yMax * 4);

    // Choose what derivative pane plots
    const derivativeCurve = (x) => {
        const correct = correctEngine.fPrime(x);
        const useStudent =
            viewMode === "STUDENT" && hasStudent && studentFor === "DERIVATIVE" && studentCurveEngine;
        return clamp(finiteOr(useStudent ? studentCurveEngine.f(x) : correct, 0), yMin * 4, yMax * 4);
    };

    // Choose what integral pane plots
    const integralCurve = (x) => {
        const correct = correctEngine.F(x);
        const useStudent =
            viewMode === "STUDENT" && hasStudent && studentFor === "INTEGRAL" && studentCurveEngine;
        return clamp(finiteOr(useStudent ? studentCurveEngine.f(x) : correct, 0), yMin * 4, yMax * 4);
    };

    const y0 = safeF(x0);
    const m0 = derivativeCurve(x0);
    const F0 = integralCurve(x0);

    const areaPoly = useMemo(() => {
        if (!showArea) return null;
        return buildAreaPolygon((x) => safeF(x), x0, 90);
    }, [showArea, x0, functionType, baseCoeffs, xMin, xMax]);

    const tangentSeg = useMemo(() => {
        if (!showTangent) return null;

        // Tangent should represent the TRUE slope of f(x), not the student's guess,
        // otherwise the geometry can mislead. Keep it correct.
        const trueSlope = clamp(finiteOr(correctEngine.fPrime(x0), 0), yMin * 4, yMax * 4);

        const x1 = xMin;
        const x2 = xMax;
        const y1 = y0 + trueSlope * (x1 - x0);
        const y2 = y0 + trueSlope * (x2 - x0);

        return {
            p1: [x1, clamp(y1, yMin * 3, yMax * 3)],
            p2: [x2, clamp(y2, yMin * 3, yMax * 3)],
        };
    }, [showTangent, x0, y0, xMin, xMax, yMin, yMax, correctEngine]);

    const cursorPoint = useMemo(() => [x0, 0], [x0]);

    const studentTag =
        hasStudent && viewMode === "STUDENT"
            ? studentFor === "DERIVATIVE"
                ? "Yours applies to f'(x)"
                : "Yours applies to F(x)"
            : null;

    return (
        <div className="ch-calculus-widget animate-slide-up">
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                <Card size="small" className="ch-shapeshifter-header">
                    <Row justify="space-between" align="middle">
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>
                                Calculus Grapher
                            </Title>

                            {hasStudent && (
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { label: "Correct", value: "CORRECT" },
                                        { label: "Yours", value: "STUDENT" },
                                    ]}
                                />
                            )}
                        </Space>

                        <Space>
                            <Tag color="magenta">FTC</Tag>
                            <Tag color="blue">{String(functionType).replace(/_/g, " ")}</Tag>
                            <Tag color="geekblue">{String(displayMode).replace(/_/g, " ")}</Tag>
                            {studentTag && <Tag color="orange">{studentTag}</Tag>}
                        </Space>
                    </Row>
                </Card>

                <AntText type="secondary">{explanation}</AntText>

                <div className="ch-statistics-layout">
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                        {/* Integral Pane */}
                        {showIntegralPane && (
                            <div className="ch-statistics-stage" style={{ height: 220 }}>
                                <Mafs height={200} viewBox={viewBox} pan={false} zoom={false}>
                                    <Coordinates.Cartesian />
                                    <Plot.OfX y={integralCurve} color={Theme.indigo} weight={3} />
                                    <Point x={x0} y={F0} color={Theme.indigo} />

                                    <MafsText x={xMin + 0.2} y={yMax - 0.8} color={Theme.indigo}>
                                        F(x)
                                    </MafsText>

                                    <MovablePoint
                                        point={cursorPoint}
                                        onMove={(p) => setX0(clamp(p[0], xMin, xMax))}
                                        constrain={(p) => [clamp(p[0], xMin, xMax), 0]}
                                        color={Theme.orange}
                                    />
                                    <Line.Segment
                                        point1={[x0, yMin]}
                                        point2={[x0, yMax]}
                                        color="#bfbfbf"
                                        weight={1}
                                        opacity={0.6}
                                    />
                                </Mafs>
                            </div>
                        )}

                        {/* Function Pane */}
                        <div className="ch-statistics-stage" style={{ height: 220 }}>
                            <Mafs height={200} viewBox={viewBox} pan={false} zoom={false}>
                                <Coordinates.Cartesian />

                                {showArea && areaPoly && (
                                    <Polygon points={areaPoly} color={Theme.indigo} opacity={0.15} />
                                )}

                                <Plot.OfX y={safeF} color={Theme.blue} weight={4} />
                                <Point x={x0} y={y0} color={Theme.blue} />

                                {showTangent && tangentSeg && (
                                    <Line.Segment
                                        point1={tangentSeg.p1}
                                        point2={tangentSeg.p2}
                                        color={Theme.orange}
                                        weight={2}
                                        opacity={0.9}
                                    />
                                )}

                                <MafsText x={xMin + 0.2} y={yMax - 0.8} color={Theme.blue}>
                                    f(x)
                                </MafsText>

                                <MovablePoint
                                    point={cursorPoint}
                                    onMove={(p) => setX0(clamp(p[0], xMin, xMax))}
                                    constrain={(p) => [clamp(p[0], xMin, xMax), 0]}
                                    color={Theme.orange}
                                />
                                <Line.Segment
                                    point1={[x0, yMin]}
                                    point2={[x0, yMax]}
                                    color="#bfbfbf"
                                    weight={1}
                                    opacity={0.6}
                                />
                            </Mafs>
                        </div>

                        {/* Derivative Pane */}
                        {showDerivativePane && (
                            <div className="ch-statistics-stage" style={{ height: 220 }}>
                                <Mafs height={200} viewBox={viewBox} pan={false} zoom={false}>
                                    <Coordinates.Cartesian />
                                    <Plot.OfX y={derivativeCurve} color={Theme.green} weight={3} />
                                    <Point x={x0} y={m0} color={Theme.green} />

                                    <MafsText x={xMin + 0.2} y={yMax - 0.8} color={Theme.green}>
                                        f'(x)
                                    </MafsText>

                                    <MovablePoint
                                        point={cursorPoint}
                                        onMove={(p) => setX0(clamp(p[0], xMin, xMax))}
                                        constrain={(p) => [clamp(p[0], xMin, xMax), 0]}
                                        color={Theme.orange}
                                    />
                                    <Line.Segment
                                        point1={[x0, yMin]}
                                        point2={[x0, yMax]}
                                        color="#bfbfbf"
                                        weight={1}
                                        opacity={0.6}
                                    />
                                </Mafs>
                            </div>
                        )}
                    </div>

                    <div className="ch-statistics-panel">
                        <Space orientation="vertical" style={{ width: "100%" }}>
                            <Card size="small" title={<Space><ThunderboltOutlined /> Slope (f')</Space>}>
                                <Statistic
                                    title={hasStudent && viewMode === "STUDENT" && studentFor === "DERIVATIVE"
                                        ? "Your predicted slope value"
                                        : "Gradient at cursor"}
                                    value={format(m0)}
                                    prefix={<LineChartOutlined />}
                                />
                            </Card>

                            <Card size="small" title={<Space><AreaChartOutlined /> Area (F)</Space>}>
                                <Statistic
                                    title={hasStudent && viewMode === "STUDENT" && studentFor === "INTEGRAL"
                                        ? "Your predicted accumulated area"
                                        : "Accumulated area from 0"}
                                    value={format(F0)}
                                    prefix={<AreaChartOutlined />}
                                />
                            </Card>

                            <Alert
                                title="How to use"
                                description="Drag the orange cursor. f(x) is always the true function. In 'Yours' mode, we compare your coefficients in the pane you attempted (f' or F)."
                                type="info"
                                showIcon
                            />
                        </Space>
                    </div>
                </div>
            </Space>
        </div>
    );
};
